import React from 'react';
import { Settings, ChevronDown, ChevronRight, CalendarDays, AlertTriangle, Save, Loader2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { JOURS_SEMAINE } from './cantineUtils';

// Section depliable + sous-sections encadrees (patterns MaCaisse).
const CantineParametresSection = ({
  isExpanded,
  onToggleSection,
  joursService,
  onToggleJour,
  autoriserRepasNonPaye,
  setAutoriserRepasNonPaye,
  observations,
  setObservations,
  isSaving,
  isDirty,
  onSave
}) => {
  return (
    <div className="px-6">
      <Card>
        <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors" onClick={onToggleSection}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isExpanded ? (
                <ChevronDown size={24} className="text-gray-600" />
              ) : (
                <ChevronRight size={24} className="text-gray-600" />
              )}
              <div>
                <CardTitle className="text-xl text-[#246990] flex items-center gap-2">
                  <Settings size={24} />
                  Paramètres de la cantine
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  jours de service et règles de pointage
                </p>
              </div>
            </div>
          </div>
        </CardHeader>

        {isExpanded && (
          <CardContent className="pt-6 space-y-6">
            {/* Jours de service */}
            <div className="border-2 border-blue-200 rounded-lg bg-blue-50/30">
              <div className="p-4 bg-blue-50 border-b border-blue-200">
                <h3 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
                  <CalendarDays size={20} />
                  Jours de service
                </h3>
                <p className="text-sm text-blue-700 mt-1">
                  Les jours où la cantine sert des repas. Un abonné au forfait est attendu ces jours-là.
                </p>
              </div>

              <div className="p-4">
                <div className="flex flex-wrap gap-2">
                  {JOURS_SEMAINE.map((jour) => {
                    const actif = joursService.includes(jour.value);
                    return (
                      <button
                        key={jour.value}
                        type="button"
                        onClick={() => onToggleJour(jour.value)}
                        className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                          actif
                            ? 'border-[#246990] bg-[#246990] text-white'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {jour.label}
                      </button>
                    );
                  })}
                </div>

                {joursService.length === 0 && (
                  <p className="mt-3 text-sm text-red-600">
                    Aucun jour sélectionné : aucun élève ne sera attendu au réfectoire.
                  </p>
                )}
              </div>
            </div>

            {/* Règle de pointage */}
            <div className="border-2 border-orange-200 rounded-lg bg-orange-50/30">
              <div className="p-4 bg-orange-50 border-b border-orange-200">
                <h3 className="text-lg font-semibold text-orange-900 flex items-center gap-2">
                  <AlertTriangle size={20} />
                  Repas non payé
                </h3>
                <p className="text-sm text-orange-700 mt-1">
                  Comportement quand un élève occasionnel se présente sans avoir payé son repas
                </p>
              </div>

              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setAutoriserRepasNonPaye(false)}
                    className={`text-left rounded-lg border-2 p-4 transition-colors ${
                      !autoriserRepasNonPaye
                        ? 'border-[#246990] bg-[#246990]/10'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <p className="text-sm font-semibold text-gray-900">Bloquer le pointage</p>
                    <p className="text-xs text-gray-600 mt-1">
                      Le repas doit être payé à la caisse avant de pouvoir pointer l'élève.
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setAutoriserRepasNonPaye(true)}
                    className={`text-left rounded-lg border-2 p-4 transition-colors ${
                      autoriserRepasNonPaye
                        ? 'border-[#c66d59] bg-[#c66d59]/10'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <p className="text-sm font-semibold text-gray-900">Autoriser le pointage</p>
                    <p className="text-xs text-gray-600 mt-1">
                      L'élève peut manger sans paiement préalable ; le repas reste à régulariser.
                    </p>
                  </button>
                </div>
              </div>
            </div>

            {/* Observations */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note interne (optionnelle)
              </label>
              <textarea
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                placeholder="Consignes particulières pour l'équipe de restauration..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                size="default"
                className="bg-[#246990] hover:bg-[#1f5a7d] text-white"
                onClick={onSave}
                disabled={isSaving || !isDirty}
              >
                {isSaving ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save size={16} className="mr-2" />
                    Enregistrer les paramètres
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default CantineParametresSection;
